import React from "react";
import { motion } from "framer-motion";
import {
  Database,
  Globe,
  BarChart3,
  Sparkles,
  Check,
  Loader2,
  AlertCircle,
} from "lucide-react";
import type { AgentId, AgentState } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ResearchProgressTimelineProps {
  agents: AgentState[];
  streaming: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

const agentIcons: Record<AgentId, React.ReactNode> = {
  data: <Database className="h-4 w-4" />,
  search: <Globe className="h-4 w-4" />,
  chart: <BarChart3 className="h-4 w-4" />,
  synthesizer: <Sparkles className="h-4 w-4" />,
  quick_chat: <Sparkles className="h-4 w-4" />,
};

const agentColors: Record<AgentId, { 
  base: string; 
  glow: string; 
  bg: string;
  border: string;
}> = {
  data: { 
    base: "rgb(16, 185, 129)", 
    glow: "rgba(16, 185, 129, 0.5)",
    bg: "bg-emerald-500",
    border: "border-emerald-500",
  },
  search: { 
    base: "rgb(34, 211, 238)", 
    glow: "rgba(34, 211, 238, 0.5)",
    bg: "bg-cyan-400",
    border: "border-cyan-400",
  },
  chart: { 
    base: "rgb(245, 158, 11)", 
    glow: "rgba(245, 158, 11, 0.5)",
    bg: "bg-amber-500",
    border: "border-amber-500",
  },
  synthesizer: { 
    base: "rgb(139, 92, 246)", 
    glow: "rgba(139, 92, 246, 0.5)",
    bg: "bg-violet-500",
    border: "border-violet-500",
  },
  quick_chat: { 
    base: "rgb(245, 158, 11)", 
    glow: "rgba(245, 158, 11, 0.5)",
    bg: "bg-amber-500",
    border: "border-amber-500",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Timeline Node
// ─────────────────────────────────────────────────────────────────────────────

const TimelineNode: React.FC<{
  agent: AgentState;
  isLast: boolean;
  index: number;
}> = ({ agent, isLast, index }) => {
  const colors = agentColors[agent.id];
  const isWorking = agent.status === "working";
  const isDone = agent.status === "done";
  const isError = agent.status === "error";
  const isPending = agent.status === "pending";
  const isIrrelevant = agent.status === "irrelevant";

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="flex items-start gap-3"
    >
      {/* Timeline connector */}
      <div className="flex flex-col items-center">
        {/* Node */}
        <motion.div
          className={`
            relative h-10 w-10 rounded-xl flex items-center justify-center
            border-2 transition-all duration-300
            ${isDone ? `${colors.border} ${colors.bg}/20` : ""}
            ${isWorking ? `${colors.border} ${colors.bg}/30` : ""}
            ${isError ? "border-rose-500 bg-rose-500/20" : ""}
            ${isPending || isIrrelevant ? "border-gray-600 bg-gray-800/50" : ""}
          `}
          style={{
            boxShadow: isWorking ? `0 0 20px ${colors.glow}` : undefined,
          }}
          {...(isWorking && {
            animate: {
              boxShadow: [
                `0 0 10px ${colors.glow}`,
                `0 0 25px ${colors.glow}`,
                `0 0 10px ${colors.glow}`,
              ],
            },
            transition: {
              duration: 1.5,
              repeat: Infinity,
            },
          })}
        >
          {/* Icon */}
          <span className={`
            relative z-10
            ${isDone ? "text-emerald-400" : ""}
            ${isWorking ? "text-white" : ""}
            ${isError ? "text-rose-400" : ""}
            ${isPending || isIrrelevant ? "text-gray-500" : ""}
          `}>
            {isDone ? <Check className="h-5 w-5" /> : 
             isError ? <AlertCircle className="h-5 w-5" /> :
             isWorking ? <Loader2 className="h-5 w-5 animate-spin" /> :
             agentIcons[agent.id]}
          </span>
        </motion.div>

        {/* Connector line */}
        {!isLast && (
          <div className="relative w-0.5 h-8 bg-gray-700/50 my-1">
            {/* Progress fill */}
            <motion.div
              className={`absolute top-0 left-0 w-full ${isDone ? colors.bg : "bg-gray-600"}`}
              initial={{ height: 0 }}
              animate={{ height: isDone || isWorking ? "100%" : "0%" }}
              transition={{ duration: 0.5 }}
            />
            {/* Animated pulse for working */}
            {isWorking && (
              <motion.div
                className={`absolute w-full ${colors.bg}`}
                style={{ height: "4px", borderRadius: "2px" }}
                animate={{ top: ["0%", "100%", "0%"] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pt-1.5 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`
            text-sm font-medium
            ${isDone ? "text-white" : ""}
            ${isWorking ? "text-white" : ""}
            ${isError ? "text-rose-300" : ""}
            ${isPending || isIrrelevant ? "text-gray-500" : ""}
          `}>
            {agent.displayName}
          </span>
          
          {/* Status tag */}
          <span className={`
            text-xs px-1.5 py-0.5 rounded
            ${isDone ? "bg-emerald-500/20 text-emerald-400" : ""}
            ${isWorking ? `bg-opacity-20 text-white` : ""}
            ${isError ? "bg-rose-500/20 text-rose-400" : ""}
            ${isPending ? "bg-gray-700/50 text-gray-500" : ""}
            ${isIrrelevant ? "bg-gray-700/50 text-gray-600" : ""}
          `}
          style={isWorking ? { backgroundColor: `${colors.base}30`, color: colors.base } : undefined}
          >
            {isDone ? "Complete" : 
             isWorking ? "Processing" : 
             isError ? "Failed" :
             isIrrelevant ? "Skipped" :
             "Waiting"}
          </span>
        </div>

        {/* Tool count or error */}
        <p className={`
          text-xs mt-0.5
          ${isError ? "text-rose-400/70" : "text-gray-500"}
        `}>
          {isError && agent.error ? agent.error :
           agent.completedTools.length > 0 ? `${agent.completedTools.length} operations completed` :
           isWorking && agent.activeTools.size > 0 ? `${agent.activeTools.size} operations running` :
           isIrrelevant ? "Not required for this query" :
           "Queued"}
        </p>

        {/* Live text preview */}
        {isWorking && agent.tokenBuffer && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-2 p-2 rounded-lg bg-gray-800/50 border border-gray-700/40"
          >
            <p className="text-xs text-gray-400 line-clamp-2 font-mono">
              {agent.tokenBuffer.slice(-150)}
              <motion.span
                className="inline-block w-1.5 h-3 ml-0.5"
                style={{ backgroundColor: colors.base }}
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.6, repeat: Infinity }}
              />
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const ResearchProgressTimeline: React.FC<ResearchProgressTimelineProps> = ({
  agents,
  streaming,
}) => {
  if (agents.length === 0) return null;

  // Calculate overall progress
  const completedCount = agents.filter(a => a.status === "done" || a.status === "irrelevant").length;
  const progress = agents.length > 0 ? (completedCount / agents.length) * 100 : 0;
  const allDone = completedCount === agents.length;

  return (
    <div className="space-y-4">
      {/* Overall Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">Research Progress</span>
          <span className={`font-medium ${allDone ? "text-emerald-400" : "text-white"}`}>
            {Math.round(progress)}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${allDone ? "bg-emerald-500" : "bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500"}`}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
          {/* Shimmer effect when not complete */}
          {streaming && !allDone && (
            <motion.div
              className="h-full w-20 bg-gradient-to-r from-transparent via-white/20 to-transparent -mt-2"
              animate={{ x: ["-100%", "500%"] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-0">
        {agents.map((agent, idx) => (
          <TimelineNode
            key={agent.id}
            agent={agent}
            isLast={idx === agents.length - 1}
            index={idx}
          />
        ))}
      </div>
    </div>
  );
};

export default ResearchProgressTimeline;

